import { AppointmentsViewer } from './Appointments';
import { AuctionsViewer } from './Auctions';
import {tx, ptx, usePageText} from '../../lib/page-text';
import {ConferenceLink} from './Conference';
import {NetworkingViewer} from './Networking';
import { SportsDisplay } from './SportsBoard';
import type { SportsView } from '../../../../api/src/live-program/sports-board';
import { ViewerIdentity } from './ViewerIdentity';
import {ViewerCourse} from "./ViewerCourse";
import { CertificateProgress } from "./CertificateProgress";
import type { CompletionProgress } from '../../../../api/src/live-program/completion-progress';
import { DashboardDialog } from "../DashboardDialog";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api-client";
import type { Program } from "../../../../api/src/live-program/program-schema";
type View = {
  name: string;
  sports?:SportsView|null;
  auctions?:{enabled:boolean};
  networking?:{enabled:boolean};
  conference?:{enabled:boolean};
  commerce: {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    priceCents: number;
    currency: string;
    stock: number;
  }[];
  features: Program["features"];
  registration: Program["registration"] | null;
  completion: Program["completion"] | null;
  completionProgress?: CompletionProgress | null;
  quizzes: Omit<Program["quizzes"][number], "answer">[];
  card: Program["cards"][number] | null;
  materials: Program["materials"];
  agenda: Program["agenda"];
  slots: (Program["slots"][number] & { available: number })[];
  attendee: {
    id: string;
    name: string;
    email: string;
    verifiedEmail?: string | null;
    registered: boolean;
    status: string;
    watchSeconds: number;
    checkedInAt: string | null;
  } | null;
  responses: { itemId: string; score: number }[];
  hasAppointments?: boolean;
  reservations: { id: string; slotId: string }[];
  certificate: {
    id: string;
    name: string;
    title: string;
    issuedAt: string;
  } | null;
};
const error = (e: any) =>
  e?.response?.data?.message ?? "Could not complete this action. Please retry.";
const esc = (s: string) =>
  s.replace(
    /[<>&"']/g,
    (c) =>
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[c]!,
  );
export function ViewerProgram({
  streamKey,
  token,
}: {
  streamKey: string;
  token?: string;
}) {
  usePageText();
  const prefix = `/streams/${encodeURIComponent(streamKey)}/program`,
    storageKey = `kardolive-attendee:${streamKey}`;
  const [secret, setSecret] = useState(() => {
      try {
        return localStorage.getItem(storageKey) ?? "";
      } catch {
        return "";
      }
    }),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [name, setName] = useState(""),
    [email, setEmail] = useState(""),
    [consent, setConsent] = useState(false),
    [answers, setAnswers] = useState<Record<string, string>>({});
  const [documentPreview, setDocumentPreview] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [certificateNotice, setCertificateNotice] = useState('');
  useEffect(
    () => () => {
      if (documentPreview) URL.revokeObjectURL(documentPreview.url);
    },
    [documentPreview],
  );
  const [cart, setCart] = useState<Record<string, number>>({}),
    [discountCode, setDiscountCode] = useState(""),
    [checkoutRequest, setCheckoutRequest] = useState(() => crypto.randomUUID());
  const media = useRef({ playing: false, seen: 0 }),
    joining = useRef(false);
  const options = {
    params: { t: token },
    headers: secret ? { "x-live-attendee": secret } : {},
  };
  const q = useQuery({
    queryKey: ["viewer-program", streamKey, token, secret],
    // Keep the form mounted while anonymous participation becomes an authenticated attendee.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === streamKey &&
      previousQuery.queryKey[2] === token &&
      !secret.startsWith('va_') && !String(previousQuery.queryKey[3] ?? '').startsWith('va_')
        ? previous
        : undefined,
    queryFn: async () => (await api.get<View>(prefix, options)).data,
    refetchInterval: 10000,
    retry: false,
  });
  useEffect(() => {
    const receive = (e: MessageEvent) => {
      if (
        e.origin === location.origin &&
        e.source === window.parent &&
        e.data?.type === "kardolive-playback-state"
      )
        media.current = { playing: !!e.data.playing, seen: Date.now() };
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);
  useEffect(() => {
    if (!secret || !q.data?.features.attendance) return;
    let stopped = false;
    const beat = () => {
      if (!stopped)
        void api
          .post(
            prefix + "/heartbeat",
            {
              playing:
                media.current.playing &&
                Date.now() - media.current.seen < 12000 &&
                !document.hidden,
            },
            options,
          )
          .catch(() => {});
    };
    const id = setInterval(beat, 15000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [secret, token, q.data?.features.attendance, prefix]);
  async function join() {
    if (joining.current) return;
    joining.current = true;
    try {
      const source =
        new URLSearchParams(window.location.search).get("source") ?? "";
      const { data } = await api.post(
        prefix + "/join",
        { source },
        { params: { t: token } },
      );
      try {
        localStorage.setItem(storageKey, data.secret);
      } catch {}
      setSecret(data.secret);
    } catch (e) {
      setNotice(error(e));
    } finally {
      joining.current = false;
    }
  }
  useEffect(() => {
    if (
      q.data &&
      (Object.values(q.data.features).some(Boolean) ||
        q.data.commerce?.length) &&
      !secret
    )
      void join();
  }, [q.data, secret]);
  async function action(path: string, body: unknown) {
    setBusy(true);
    setNotice("");
    if (path === 'certificate') setCertificateNotice('');
    try {
      const { data } = await api.post(prefix + "/" + path, body, options);
      await q.refetch();
      setNotice("Saved");
      return data;
    } catch (e) {
      if (path === 'certificate') {
        setCertificateNotice(error(e));
        await q.refetch();
      } else setNotice(error(e));
      return null;
    } finally {
      setBusy(false);
    }
  }
  function changeSession(value: string) {
    try { if (value) localStorage.setItem(storageKey, value); else localStorage.removeItem(storageKey); } catch {}
    setSecret(value); setName(''); setEmail(''); setConsent(false); setAnswers({}); setNotice('');
  }
  useEffect(() => { if (q.data?.attendee?.verifiedEmail) setEmail(q.data.attendee.verifiedEmail); }, [q.data?.attendee?.verifiedEmail]);
  const identity = <ViewerIdentity streamKey={streamKey} token={token} secret={secret} onSession={changeSession} />;
  if (q.isPending) return <section className="card live-program viewer-program">
      {identity}<p role="status">{tx("Loading event tools…")}</p></section>;
  if (q.isError)
    return (
      <section className="card" role="alert">
        {identity}
        {error(q.error)}{" "}
        <button className="btn outline sm" onClick={() => void q.refetch()}>{tx("Retry event tools")}</button>
        {secret && (
          <button
            className="btn ghost sm"
            onClick={() => {
              try {
                localStorage.removeItem(storageKey);
              } catch {}
              setSecret("");
            }}
          >{tx("Join as a new visitor")}</button>
        )}
      </section>
    );
  const d = q.data;
  if (!d)return null;
  if(!Object.values(d.features).some(Boolean)&&!d.commerce?.length&&!d.conference?.enabled&&!d.auctions?.enabled)return d.sports?.enabled?<SportsDisplay board={d.sports}/>:null;
  const a = d.attendee,
    blocked = a && ["pending", "waitlisted", "revoked"].includes(a.status);
  return (
    <section className="card live-program viewer-program">
      {d.sports?.enabled&&<SportsDisplay board={d.sports}/>}
      {identity}
      {d.auctions?.enabled&&<AuctionsViewer key={streamKey+secret} streamKey={streamKey} token={token} secret={secret}/>}
      {d.networking?.enabled&&<NetworkingViewer key={streamKey+secret} streamKey={streamKey} token={token} secret={secret} approved={!!a?.registered&&a.status==='approved'}/>}
      {d.conference?.enabled&&<ConferenceLink streamKey={streamKey} token={token}/>}
      {documentPreview && (
        <DashboardDialog
          title={documentPreview.title}
          onClose={() => setDocumentPreview(null)}
        >
          <iframe
            title={documentPreview.title}
            src={documentPreview.url}
            sandbox="allow-same-origin"
            style={{ width: "100%", height: "65vh", border: 0 }}
          />
          <a
            className="btn outline"
            href={documentPreview.url}
            download="material.pdf"
          >{tx("Download PDF")}</a>
        </DashboardDialog>
      )}
      <h3>{tx("Participate")}</h3>
      <ViewerCourse account={!!a?.verifiedEmail} streamKey={streamKey} token={token} secret={secret} materials={d.materials} onMaterial={async m=>{if(!m.url.startsWith('/')){window.open(m.url,'_blank','noopener,noreferrer');return;}setBusy(true);try{const {data}=await api.get(m.url.replace(/^\/v1/,''),{...options,responseType:'blob'});setDocumentPreview({url:URL.createObjectURL(data),title:m.title});}catch(e){setNotice(error(e));}finally{setBusy(false);}}}/>
      <p role="status">{notice}</p>
      {!secret && (
        <button
          className="btn outline"
          disabled={busy}
          onClick={() => void join()}
        >{tx("Join event tools")}</button>
      )}
      {d.commerce?.length > 0 && (
        <details>
          <summary>{ptx("Shop this stream · {v0}{v1}items", {v0: (Object.values(cart).reduce((n, q) => n + q, 0)), v1: (" ")})}</summary>
          {d.commerce.map((p) => (
            <article className="program-item" key={p.id}>
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt=""
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: 10,
                  }}
                />
              )}
              <strong>{p.name}</strong>
              <p>{p.description}</p>
              <p>
                {new Intl.NumberFormat(undefined, {
                  style: "currency",
                  currency: p.currency,
                }).format(
                  p.priceCents /
                    10 **
                      (new Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: p.currency,
                      }).resolvedOptions().maximumFractionDigits ?? 2),
                )}
              </p>
              <label>{ptx("Quantity{v0}", {v0: (<input
                  className="input"
                  type="number"
                  min={0}
                  max={p.stock < 0 ? 100 : Math.min(100, p.stock)}
                  value={cart[p.id] ?? 0}
                  onChange={(e) => {
                    setCart({
                      ...cart,
                      [p.id]: Math.max(
                        0,
                        Math.min(100, Number(e.target.value) || 0),
                      ),
                    });
                    setCheckoutRequest(crypto.randomUUID());
                  }}
                />)})}</label>
              {p.stock === 0 && <p>{tx("Sold out")}</p>}
            </article>
          ))}
          <label>{ptx("Discount code{v0}", {v0: (<input
              className="input"
              maxLength={40}
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value);
                setCheckoutRequest(crypto.randomUUID());
              }}
            />)})}</label>
          <button
            className="btn primary"
            disabled={
              busy || !secret || !Object.values(cart).some((n) => n > 0)
            }
            onClick={async () => {
              const r = await action("checkout", {
                requestId: checkoutRequest,
                items: Object.entries(cart)
                  .filter(([, q]) => q > 0)
                  .map(([productId, quantity]) => ({ productId, quantity })),
                ...(discountCode.trim()
                  ? { discountCode: discountCode.trim() }
                  : {}),
              });
              if (r?.url) window.location.assign(r.url);
            }}
          >{tx("Secure checkout")}</button>
          <p className="muted">{tx("Your payment provider opens checkout and returns you to this stream. Payment availability depends on the seller’s setup.")}</p>
        </details>
      )}
      {d.features.registration && d.registration && (
        <details open={!a?.registered}>
          <summary>
            {a?.registered
              ? ptx("Registration · {v0}", {v0: (a.status)})
              : tx("Register for this stream")}
          </summary>
          {a?.registered ? (
            <p>{ptx("Thank you, {v0}.{v1}{v2}", {v0: (a.name), v1: (" "), v2: (a.status === "approved"
                ? tx("Your registration is confirmed.")
                : a.status === "waitlisted"
                  ? tx("You are on the waiting list.")
                  : tx("Your registration is awaiting host approval."))})}</p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void action("register", { name, email, consent, answers });
              }}
            >
              <label>{ptx("Name{v0}", {v0: (<input
                  required
                  className="input"
                  maxLength={100}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />)})}</label>
              <label>{ptx("Email{v0}", {v0: (<input
                  required
                  className="input"
                  type="email"
                  maxLength={254}
                  autoComplete="email"
                  readOnly={!!a?.verifiedEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />)})}</label>
              {d.registration.fields.map((f) => (
                <label key={f.id}>
                  {f.label}
                  <input
                    className="input"
                    required={f.required}
                    maxLength={1000}
                    value={answers[f.id] ?? ""}
                    onChange={(e) =>
                      setAnswers({ ...answers, [f.id]: e.target.value })
                    }
                  />
                </label>
              ))}
              <label className="program-consent">{ptx("{v0}I agree to share these details and my event participation with the organizer.", {v0: (<input
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />)})}</label>
              <button
                className="btn primary"
                disabled={busy || !secret || !consent}
              >{tx("Register")}</button>
            </form>
          )}
        </details>
      )}
      {d.features.attendance && (
        <div className="program-row">
          <p className="muted">{ptx("Participation: {v0} minutes watched {v1}.", {v0: (Math.floor((a?.watchSeconds ?? 0) / 60)), v1: (a?.verifiedEmail ? tx("for this event in your viewer account") : tx("on this browser"))})}</p>
          <button
            className="btn outline sm"
            disabled={busy || !secret || !!blocked || !!a?.checkedInAt}
            onClick={() =>
              void action("respond", { kind: "checkin", itemId: "event" })
            }
          >
            {a?.checkedInAt ? tx("Checked in") : tx("Check in")}
          </button>
        </div>
      )}
      {d.card && (
        <article className="program-action">
          <span className="badge">{d.card.kind}</span>
          <h3>{d.card.title}</h3>
          <p>{d.card.body}</p>
          <a
            className="btn primary"
            href={d.card.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (secret)
                void action("respond", { kind: "card", itemId: d.card!.id });
            }}
          >
            {d.card.label} ↗
          </a>
        </article>
      )}
      {d.quizzes.map((q) => {
        const response = d.responses.find((r) => r.itemId === q.id);
        return (
          <details key={q.id} open>
            <summary>{q.title}</summary>
            {response ? (
              <p role="status">{ptx("Submitted · Score {v0}%", {v0: (response.score)})}</p>
            ) : (
              <div className="program-answers">
                {q.options.map((o, i) => (
                  <button
                    key={i}
                    className="btn outline"
                    disabled={busy || !secret || !!blocked}
                    onClick={() =>
                      void action("respond", {
                        kind: "quiz",
                        itemId: q.id,
                        answer: i,
                      })
                    }
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
          </details>
        );
      })}
      {d.materials.length > 0 && (
        <details>
          <summary>{ptx("Materials · {v0}", {v0: (d.materials.length)})}</summary>
          {d.materials.map((m) => (
            <p key={m.id}>
              {m.url.startsWith("/") ? (
                <button
                  className="btn outline sm"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const { data } = await api.get(
                        m.url.replace(/^\/v1/, ""),
                        { ...options, responseType: "blob" },
                      );
                      setDocumentPreview({
                        url: URL.createObjectURL(data),
                        title: m.title,
                      });
                      if (secret)
                        void action("respond", {
                          kind: "material",
                          itemId: m.id,
                        });
                    } catch (e) {
                      setNotice(error(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {m.title}
                </button>
              ) : (
                <a
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    if (secret)
                      void action("respond", {
                        kind: "material",
                        itemId: m.id,
                      });
                  }}
                >
                  {m.title} ↗
                </a>
              )}
            </p>
          ))}
        </details>
      )}
      {d.agenda.length > 0 && (
        <details>
          <summary>{tx("Agenda")}</summary>
          {d.agenda
            .slice()
            .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
            .map((e) => (
              <article className="program-item" key={e.id}>
                <strong>{e.title}</strong>
                <p>{ptx("{v0} · {v1} minutes", {v0: (new Date(e.startsAt).toLocaleString()), v1: (e.minutes)})}</p>
                <p className="muted">{e.speaker}</p>
                {e.streamId && (
                  <a
                    className="btn outline sm"
                    href={`/v1/events/${encodeURIComponent(e.streamId)}/share`}
                  >{tx("Open session")}</a>
                )}
              </article>
            ))}
        </details>
      )}
      {(d.features.bookings || d.hasAppointments || d.reservations.length > 0) && <AppointmentsViewer key={streamKey+secret} streamKey={streamKey} token={token} secret={secret} approved={!!a?.registered && !blocked} onChange={()=>void q.refetch()}/>}
      {d.features.certificates && d.completion && (
        <details className="certificate-panel">
          <summary>{tx("Completion certificate")}</summary>
          <CertificateProgress account={!!a?.verifiedEmail} progress={d.completionProgress} />
          {certificateNotice && <p role="alert">{certificateNotice}</p>}
          {d.certificate ? (
            <>
              <p>
                {d.certificate.title} · {d.certificate.name}
              </p>
              <button
                className="btn outline"
                onClick={() => {
                  const c = d.certificate!;
                  const verification = `${location.origin}/v1/live-certificates/${c.id}`;
                  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="850" viewBox="0 0 1200 850"><rect width="1200" height="850" fill="#f8fafc"/><rect x="35" y="35" width="1130" height="780" rx="20" fill="white" stroke="#0284c7" stroke-width="3"/><g text-anchor="middle" font-family="sans-serif" fill="#0f172a"><text x="600" y="180" font-size="24" fill="#0284c7">KARDOLIVE</text><text x="600" y="285" font-size="36">${esc(c.title)}</text><text x="600" y="395" font-size="44">${esc(c.name)}</text><text x="600" y="475" font-size="24">${esc(d.name)}</text><text x="600" y="570" font-size="20">Issued ${esc(new Date(c.issuedAt).toLocaleDateString())}</text><text x="600" y="685" font-size="14">${esc(verification)}</text><text x="600" y="725" font-size="14">Certificate ${esc(c.id)}</text></g></svg>`;
                  const url = URL.createObjectURL(
                    new Blob([svg], { type: "image/svg+xml" }),
                  );
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "certificate.svg";
                  link.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
              >{tx("Download certificate")}</button>
            </>
          ) : (
            <div className="certificate-actions">
              <button
                className="btn"
                disabled={busy || !secret || !d.completionProgress?.canIssue || q.isFetching}
                onClick={() => void action("certificate", {})}
              >
                {busy ? tx("Please wait…") : tx("Issue certificate")}
              </button>
              <button className="btn outline" disabled={busy || q.isFetching} onClick={() => { setCertificateNotice(''); void q.refetch(); }}>
                {q.isFetching ? tx("Refreshing…") : tx("Refresh progress")}
              </button>
            </div>
          )}
        </details>
      )}
      <button
        className="btn ghost sm"
        onClick={async () => {
          try {
            const { data } = await api.get(prefix + "/calendar", {
              ...options,
              responseType: "blob",
            });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "live-stream.ics";
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (e) {
            setNotice(error(e));
          }
        }}
      >{tx("Add scheduled stream to calendar")}</button>
    </section>
  );
}
